"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import BreadcrumbNav from "@/components/customLayoutServices/BreadcrumbNav";

type PlaceFiltersProps = {
  title: string;
  selectedTerminal: string;
  setSelectedTerminal: (val: string) => void;
  selectedCategories: string[];
  setSelectedCategories: (val: string[]) => void;
  search: string;
  setSearch: (val: string) => void;
  categories?: string[];
  useFilter?: boolean; // Optional prop to control filtering
};

export default function PlaceFilters({
  title,
  selectedTerminal,
  setSelectedTerminal,
  selectedCategories,
  setSelectedCategories,
  search,
  setSearch,
  categories,
  useFilter = true, // Optional prop to control filtering
}: PlaceFiltersProps) {
  const t = useTranslations("searchBar");
  const [categoryDropdown, setCategoryDropdown] = useState(false);

  return (
    <div className="relative z-10 -mt-10 w-[80%] mx-auto bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-2xl border border-gray-100">
      <BreadcrumbNav className="mb-5" />
      <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-[#102542] to-[#243d6a] bg-clip-text text-transparent">
        {title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <h3 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-[#102542]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            {t("terminal")}
          </h3>
          <div className="flex gap-3">
            {[
              { label: t("terminalTodas"), value: "todas" },
              { label: "T1", value: "T1" },
              { label: "T2", value: "T2" },
            ].map((term) => (
              <button
                key={term.value}
                onClick={() => setSelectedTerminal(term.value)}
                className={`relative px-5 py-2.5 rounded-xl border-2 font-medium transition-all duration-300 ease-in-out transform hover:-translate-y-0.5
            ${
              selectedTerminal === term.value
                ? "bg-gradient-to-r from-[#102542] to-[#1a3456] text-white border-[#102542] shadow-lg scale-105"
                : "bg-white text-gray-700 border-gray-200 hover:bg-gradient-to-r hover:from-[#102542]/5 hover:to-[#1a3456]/5 hover:border-[#102542]/30 hover:scale-105 hover:shadow-md"
            }`}
              >
                {term.label}
              </button>
            ))}
          </div>
        </div>

        {useFilter === true ? (
          <div>
            <h3 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-[#102542]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z"
                />
              </svg>
              {t("filtros")}
            </h3>
            <div className="relative">
              <button
                className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl flex items-center justify-between bg-white hover:border-[#102542]/30 transition-all duration-300 font-medium shadow-sm hover:shadow-md"
                onClick={() => setCategoryDropdown(!categoryDropdown)}
              >
                <span
                  className={
                    selectedCategories.length > 0
                      ? "text-[#102542]"
                      : "text-gray-500"
                  }
                >
                  {selectedCategories.length > 0
                    ? `${t("categorias")}: ${selectedCategories.length} ${t(
                        "seleccionadas"
                      )}`
                    : t("filtrosPlaceholder")}
                </span>
                <div
                  className={`transition-transform duration-300 ${
                    categoryDropdown ? "rotate-180" : ""
                  }`}
                >
                  <ChevronDown size={20} className="text-[#102542]" />
                </div>
              </button>
              {categoryDropdown && (
                <div className="absolute mt-3 w-full bg-white border-2 border-gray-100 rounded-2xl shadow-2xl max-h-56 overflow-y-auto z-20 backdrop-blur-sm">
                  <div className="p-3">
                    {categories &&
                      categories.map((cat) => {
                        const isChecked = selectedCategories.includes(cat);
                        return (
                          <label
                            key={cat}
                            className="flex items-center gap-3 mb-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 hover:bg-gradient-to-r hover:from-[#102542]/5 hover:to-[#1a3456]/5 group"
                            onClick={() => {
                              if (isChecked) {
                                setSelectedCategories(
                                  selectedCategories.filter((c) => c !== cat)
                                );
                              } else {
                                setSelectedCategories([
                                  ...selectedCategories,
                                  cat,
                                ]);
                              }
                            }}
                          >
                            <span
                              className={`w-5 h-5 flex items-center justify-center border-2 rounded-lg transition-all duration-200 ${
                                isChecked
                                  ? "bg-gradient-to-r from-[#102542] to-[#1a3456] border-[#102542] text-white shadow-md scale-110"
                                  : "border-gray-300 bg-white group-hover:border-[#102542]/50"
                              }`}
                            >
                              {isChecked && (
                                <svg
                                  className="w-3 h-3"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  viewBox="0 0 24 24"
                                >
                                  <path d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                            <span
                              className={`font-medium transition-colors duration-200 ${
                                isChecked
                                  ? "text-[#102542]"
                                  : "text-gray-700 group-hover:text-[#102542]"
                              }`}
                            >
                              {cat}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        <div>
          <h3 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
            <svg
              className="w-5 h-5 text-[#102542]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {t("buscar")}
          </h3>
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border-2 border-gray-200 px-4 py-3 pl-11 rounded-xl focus:border-[#102542] focus:outline-none transition-all duration-300 bg-white shadow-sm hover:shadow-md font-medium placeholder:text-gray-400"
              placeholder={t("buscarPlaceholder")}
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
